<template>
  <el-dialog
    v-model="dialogShow"
    title="注册登录"
    width="600px"
    transition="dialog-bounce"
    :show-close="false"
    align-center
    lock-scroll
  >
    <template #header="{ close }">
      <div class="dialog-header">
        <el-icon :size="20" @click="close" class="close-btn">
          <Close />
        </el-icon>
        <h2>注册登录</h2>
      </div>
    </template>
    <div class="dialog-content">
      <div class="login-form">
        <el-segmented
          class="custom-style"
          v-model="loginType"
          :options="loginTypeOptions"
          size="large"
          style="width: 100%"
        />
        <el-form :model="form" :rules="rules" ref="formRef">
          <el-form-item label="" prop="mobile" v-if="loginType === 'mobile'">
            <el-input
              type="number"
              size="large"
              maxlength="11"
              v-model="form.mobile"
              placeholder="输入手机号"
            />
          </el-form-item>
          <el-form-item label="" prop="email" v-if="loginType === 'email'">
            <el-input
              size="large"
              v-model="form.email"
              placeholder="输入邮箱"
            />
          </el-form-item>
          <el-form-item label="" prop="code">
            <el-input
              type="number"
              maxlength="6"
              size="large"
              style="flex: 1; margin-right: 10px"
              v-model="form.code"
              placeholder="输入验证码"
            />
            <el-button
              size="large"
              round
              type="primary"
              @click="getCode"
              color="#0b0b0b"
            >
              {{
                loginType === "mobile"
                  ? mobileTime > 0
                    ? `${mobileTime}s`
                    : "获取验证码"
                  : emailTime > 0
                  ? `${emailTime}s`
                  : "获取验证码"
              }}
            </el-button>
          </el-form-item>
        </el-form>
        <el-button
          size="large"
          class="login-btn"
          round
          type="primary"
          color="#026ccb"
          @click="login"
        >
          注册 / 登录
        </el-button>
        <div class="agreement">
          未注册手机验证后自动登录，登录或注册即代表同意
          <span class="agreement-link">用户协议</span>
          和
          <span class="agreement-link">个人信息保护政策</span>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ElMessage } from "element-plus";
import { defineProps, defineEmits, ref } from "vue";
import { useVModel } from "@vueuse/core";
import {
  getMobileCodeApi,
  mobileLoginApi,
  getEmailCodeApi,
  emailLoginApi,
} from "@/api/login";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  detailId: {
    type: Number,
    required: true,
    default: 0,
  },
});
const emit = defineEmits(["update:show", "loginSuccess"]);

const dialogShow = useVModel(props, "show", emit);

const loginType = ref("mobile");
const loginTypeOptions = ref([
  { label: "手机号", value: "mobile" },
  { label: "邮箱", value: "email" },
]);

const form = ref({
  mobile: "",
  code: "",
});

const rules = ref({
  mobile: [
    { required: true, message: "请输入手机号", trigger: "blur" },
    {
      pattern: /^1[3-9]\d{9}$/,
      message: "请输入正确的手机号",
      trigger: "blur",
    },
  ],
  email: [
    { required: true, message: "请输入邮箱", trigger: "blur" },
    {
      pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: "请输入正确的邮箱",
      trigger: "blur",
    },
  ],
  code: [{ required: true, message: "请输入验证码", trigger: "blur" }],
});
// 获取验证码倒计时
const mobileTime = ref(0);
const mobileTimer = ref(null);
const emailTime = ref(0);
const emailTimer = ref(null);
const getCode = () => {
  if (loginType.value === "mobile") {
    if (!form.value.mobile) {
      ElMessage.warning("请输入手机号");
      return;
    }
    if (mobileTimer.value) return;
    getMobileCodeApi({
      mobile: form.value.mobile,
      countryCode: "86",
    }).then(() => {
      ElMessage.success("验证码发送成功");
      // 倒计时
      mobileTime.value = 60;
      mobileTimer.value = setInterval(() => {
        mobileTime.value--;
        if (mobileTime.value <= 0) {
          clearInterval(mobileTimer.value);
          mobileTimer.value = null;
        }
      }, 1000);
    });
  } else if (loginType.value === "email") {
    if (!form.value.email) {
      ElMessage.warning("请输入邮箱");
      return;
    }
    if (emailTimer.value) return;
    getEmailCodeApi({
      email: form.value.email,
    }).then(() => {
      ElMessage.success("验证码发送成功");
      // 倒计时
      emailTime.value = 60;
      emailTimer.value = setInterval(() => {
        emailTime.value--;
        if (emailTime.value <= 0) {
          clearInterval(emailTimer.value);
          emailTimer.value = null;
        }
      }, 1000);
    });
  }
};

const formRef = ref();
const login = async () => {
  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res =
          loginType.value === "mobile"
            ? await mobileLoginApi({
                mobile: form.value.mobile,
                code: form.value.code,
              })
            : await emailLoginApi({
                email: form.value.email,
                code: form.value.code,
              });
        ElMessage.success("登录成功");
        localStorage.setItem("token", res.token);
        localStorage.setItem("user", JSON.stringify(res.user));
        dialogShow.value = false;
        emit("loginSuccess");
      } catch (error) {
        ElMessage.error(error.message);
      }
    }
  });
};
</script>

<style scoped lang="scss">
.dialog-header {
  h2 {
    font-size: 20px;
    font-weight: 700;
    text-align: center;
  }
  .close-btn {
    float: right;
    cursor: pointer;
  }
}
.dialog-content {
  padding: 20px;
  .login-form {
    margin: 0 auto;
    width: 70%;
    .custom-style {
      height: 48px;
      --el-segmented-item-selected-color: #fff;
      --el-segmented-item-selected-bg-color: #026ccb;
      --el-border-radius-base: 24px;
      margin-bottom: 20px;
    }
    :deep(.el-input) {
      height: 48px;
      .el-input__wrapper {
        box-shadow: none;
        border-radius: 24px;
        background-color: #f9f9f9;
      }
    }
    .login-btn {
      height: 48px;
      border-radius: 24px;
      width: 100%;
      margin-top: 20px;
    }
    .agreement {
      margin-top: 20px;
      font-weight: 600;
      color: #666;
      .agreement-link {
        color: #026ccb;
        cursor: pointer;
      }
    }
  }
}
</style>
